//----------------------------------------
// barkoder SDK ファサード
//----------------------------------------
// scandit/sdk.jsx と同様に、SDK初期化とカメラをアプリ内で1つだけ保持するモジュール。
// カメラ描画先は body 直下の常駐コンテナに置き、スキャン画面表示中だけ
// 画面内のスロットへ付け替える。これによりスキャン画面が未表示でもカメラを起動できる。
// カメラの開閉タイミングは scandit（enableCamera）に合わせている。

import { createHost, attachHost, detachHost, playVideos, refreshHostLayout, logCameraCapabilities, applyCameraFocus } from "./scanHost";
import { getCameraResolution, getBarkoderDpsLimit, CAMERA_RESOLUTION_HD } from "./cameraResolutionSetting";
import { getCameraFocus } from "./cameraFocusSetting";

let barkoder = null;
let initPromise = null;
let scriptPromise = null;
//BarkoderSDK.initialize() の生のPromiseと、最初に呼んだ時刻
let sdkInitPromise = null;
let sdkInitStartedAt = 0;
//初期化に失敗した理由
//※実機ではログを取れないことがあるため、スキャン画面のエラー表示に載せる
let lastInitError = null;
//カメラ起動中フラグ
let cameraOn = false;
//カメラ起動／停止の直列化
let opChain = Promise.resolve();

function enqueue(fn) {
	const next = opChain.then(fn, fn);
	opChain = next.catch(() => {});
	return next;
}
//スキャン結果の受け取り先（表示中のスキャン画面
let scanHandler = null;
//エラーの通知先（表示中のスキャン画面
let errorHandler = null;
//"barcode" / "qr"
let scanMode = null;
//デコード中かどうか
let decodingActive = false;
//バックグラウンド復帰の監視を登録済みか
let visibilityHandlersRegistered = false;
//復帰処理の多重実行防止（visibilitychangeとfocusが同時に発火するため
let resuming = false;
//プレビュー停止（フリーズ）検知
let watchdogTimer = null;
let lastVideoTime = -1;
let stallCount = 0;

//フリーズ検知の間隔と、何回連続で止まっていたら復旧するか
const WATCHDOG_INTERVAL = 2000;
const WATCHDOG_STALL_LIMIT = 2;
//背面カメラのdeviceId（一度特定したら使い回す
let backCameraId = null;

//SDK本体（public/barkoder.js）の配置先
//※barkoderはwasmを「読み込んだscriptと同じ階層」から取得するため、
//　webpackのチャンク（/static/js/）に同梱せず、必ずルートのscriptとして読み込む。
//　（initialize()のwasmPath指定はSDK側で使われないため効果がない）
const BARKODER_JS_PATH = "/barkoder.js";
//window.BarkoderSDK 生成待ちのタイムアウト
const BARKODER_JS_TIMEOUT = 15000;

//initialize() の待ち上限
//※barkoderの initialize() は内部で「wasmの読み込み完了」を無制限に待つ実装になっている。
//
//    initialize: async(key) => { while(!Connector.moduleLoaded){ await Common.wait(20); } ... }
//
//　wasm（約2.1MB）が取得できないと永久に解決せず、reject もしないため
//　getBarkoder() の try/catch にも掛からない。startCamera() は enqueue() の
//　直列キュー内で止まるので、以降のカメラ操作がすべて詰まり、
//　スキャン画面は黒いまま・エラーも出ない、という状態になる。
//　（barkoderは内部エラーログも出力しないため手掛かりが残らない）
//　電車内など回線が不安定な場所でwasmの取得に失敗すると再現する。
//　ここで打ち切ることで、少なくともエラーとして扱えるようにする
const BARKODER_INIT_TIMEOUT = 20000;

//初期化後にライセンスを適用し直すまでの待ち時間
//※SDKがサーバー応答を1秒しか待たないため、間に合わなかったぶんをここで拾う
const LICENSE_REAPPLY_DELAY = 3000;

//----------------------------------------
//タイムアウト付きawait
//----------------------------------------
// 完了しない可能性のある処理が、全体を固まらせないようにする。

function withTimeout(promise, ms, label) {
	let timer = null;

	const timeout = new Promise((resolve, reject) => {
		timer = setTimeout(() => {
			reject(new Error(label + " timeout (" + ms + "ms)"));
		}, ms);
	});

	return Promise.race([promise, timeout]).finally(() => {
		if (timer) {
			clearTimeout(timer);
			timer = null;
		}
	});
}

//----------------------------------------
//ライセンス確認の診断（ログを出すだけ。動作は変えない
//----------------------------------------
// barkoder はライセンス確認の内部ログ（Stage 2 timeout など）を出さない実装になっている。
//
//   static debugMessage(tag, message, log) { if (typeof log == 'boolean' && log) { return; ... } }
//
// そのため「スキャンしても何も起きない」が起きても、ライセンスが原因かを確定できない。
// （2026-10-26 20:35 に一度だけ起きた件。同じイメージで後から読めたため原因が残らなかった）
// 初期化・再適用のたびに、ライセンスのキャッシュの状態と SDK の判定経路を記録しておく。
// ※キーの値・キャッシュの中身・読み取り値は出さない
//
// SDK の判定経路（barkoder.js の initialize() より）
//   Connector.initialize() の中で、wasm がキャッシュかキーでライセンスを確認できると callback3D が立つ。
//   立たなければ callback2D に false を入れて待ち始め、tracking.barkoder.com に問い合わせる。
//   応答が来ると callback2D が立つが、SDK は最大1秒しか待たず、来なければライセンス未適用のまま完了する。

const LICENSE_CACHE_KEY = "BarkoderSDK.cache.license";
//SDK の待ち上限（barkoder.js の initialize() の timeCounter >= 1000）
const LICENSE_SERVER_WAIT_LIMIT = 1000;

//直近の状態
//※WebView を外から覗いたときに window.__barkoderDiag で一度に見られるようにする
const licenseDiag = {
	init: null,
	reapply: null,
	reapplyCount: 0,
	unlicensedCount: 0,
	lastUnlicensedAt: null,
};
if (typeof window !== "undefined") {
	window.__barkoderDiag = licenseDiag;
}

//ライセンスのキャッシュの状態
//※中身は出さず、有無・文字数と、前後で変わったかを見るための簡易な指紋だけ
function readLicenseCache() {
	try {
		const value = window.localStorage.getItem(LICENSE_CACHE_KEY);
		if (!value) return { exists: false, length: 0, sig: "" };
		let hash = 0;
		for (let i = 0; i < value.length; i++) {
			hash = (hash * 31 + value.charCodeAt(i)) | 0;
		}
		return { exists: true, length: value.length, sig: (hash >>> 0).toString(16) };
	} catch (e) {
		return { exists: false, length: 0, sig: "", error: String(e) };
	}
}

//SDK が初期化中に立てる目印を見張り、開始からの経過（ms）を記録する
//※SDK はこの変数を typeof で調べる・代入する・読むだけなので、読み書きをそのまま通せば挙動は変わらない。
//　SDK の版が上がって変数名が変わった場合は、経路が「不明」になるだけ
let licenseCheckRecord = null;
let licenseWatchActive = false;
//見張りを付けられなかった（以降は付け直さず、ログも繰り返さない
let licenseWatchFailed = false;

//目印1つぶんの見張り
//※ループ内で関数を作ると ESLint（no-loop-func）の警告になるため切り出している
function watchLicenseFlag(name) {
	let value = window[name];
	Object.defineProperty(window, name, {
		configurable: true,
		enumerable: true,
		get: () => value,
		set: (v) => {
			const rec = licenseCheckRecord;
			if (rec) {
				const elapsed = Date.now() - rec.startedAt;
				//callback2D に false が入る＝SDK がサーバー応答を待ち始めた
				if (name === "BarkoderSDK_callback2D" && v === false && rec.serverWaitStartedAt === null) {
					rec.serverWaitStartedAt = elapsed;
				}
				if (v === true && value !== true) {
					rec[name] = elapsed;
				}
			}
			value = v;
		},
	});
}

function watchLicenseFlags() {
	if (licenseWatchActive) return true;
	if (licenseWatchFailed || typeof window === "undefined") return false;

	try {
		watchLicenseFlag("BarkoderSDK_callback3D");
		watchLicenseFlag("BarkoderSDK_callback2D");
		licenseWatchActive = true;
	} catch (e) {
		licenseWatchFailed = true;
		console.log("【barkoder】ライセンス確認の見張りを付けられない: " + e);
	}
	return licenseWatchActive;
}

function describeLicenseCache(cache) {
	return cache.exists ? "あり(" + cache.length + "文字)" : "なし";
}

function describeLicensePath(rec) {
	if (!rec.watching) return "経路は不明（見張りなし）";
	if (rec.BarkoderSDK_callback3D !== null) return "キャッシュかキーで確認（サーバー問い合わせなし）";
	if (rec.serverWaitStartedAt === null) return "経路は不明（SDKの目印が立たなかった）";
	if (rec.BarkoderSDK_callback2D === null) return "★サーバー応答なし（" + LICENSE_SERVER_WAIT_LIMIT + "msで打ち切り）";

	const wait = rec.BarkoderSDK_callback2D - rec.serverWaitStartedAt;
	if (wait > LICENSE_SERVER_WAIT_LIMIT) return "★サーバー応答が間に合わず（待ち" + wait + "ms）";
	return "サーバーが応答（待ち" + wait + "ms）";
}

//initialize() の直前に呼ぶ
function beginLicenseCheck(label) {
	const rec = {
		label,
		at: new Date().toLocaleTimeString(),
		startedAt: Date.now(),
		online: typeof navigator !== "undefined" ? navigator.onLine : null,
		watching: watchLicenseFlags(),
		cacheBefore: readLicenseCache(),
		serverWaitStartedAt: null,
		BarkoderSDK_callback3D: null,
		BarkoderSDK_callback2D: null,
	};
	licenseCheckRecord = rec;

	console.log("【barkoder】ライセンス確認(" + label + ")開始: キャッシュ" + describeLicenseCache(rec.cacheBefore) + " / " + (rec.online === false ? "offline" : "online"));
	return rec;
}

//initialize() の完了後（成功・失敗とも）に呼ぶ
function endLicenseCheck(rec, error) {
	if (licenseCheckRecord === rec) licenseCheckRecord = null;

	rec.ms = Date.now() - rec.startedAt;
	rec.cacheAfter = readLicenseCache();
	rec.cacheChanged = rec.cacheBefore.sig !== rec.cacheAfter.sig;
	rec.path = describeLicensePath(rec);
	if (error) rec.error = String(error);

	const cacheText = rec.cacheAfter.exists ? (rec.cacheChanged ? "更新された" : "変化なし") : "作られず";
	console.log("【barkoder】ライセンス確認(" + rec.label + ")結果: " + rec.path + " / " + rec.ms + "ms / キャッシュ" + cacheText + (error ? " / エラー: " + error : ""));
}

//未適用を検知したときに添える状況
function describeUnlicensedContext() {
	const init = licenseDiag.init;
	const since = init ? ((Date.now() - init.startedAt) / 1000).toFixed(1) + "秒" : "不明";
	const last = licenseDiag.reapply || init;
	return "初期化から" + since
		+ "・" + licenseDiag.unlicensedCount + "回目"
		+ "・直近の確認: " + (last ? last.path || "確認中" : "未実施")
		+ "・キャッシュ" + describeLicenseCache(readLicenseCache())
		+ "・" + (typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "online");
}

//----------------------------------------
//ライセンスの準備の状態
//----------------------------------------
// 診断の結果（経路）から、ライセンスの準備ができているかを判定する。
// できていなければ、読み取りを待たずにスキャン画面でエラーを出す。
// ※読み取り値のマスクを検知してから出す方式（D-30）だけだと、利用者が2回読み取りを
// 　試すまで何も起きないように見える（2026-10-26 20:35 の件）。
//
//   unknown  … 判定できない（SDK の目印を見張れない等）。D-30 の方式だけで動く
//   checking … まだ準備できていないが、自動の再試行が残っている
//   ok       … 準備できた（キャッシュかキーで確認／サーバーが応答／マスクされていない値が読めた）
//   ng       … 自動の再試行を使い切っても準備できなかった、またはマスクされた値を検知した
//
// エラーを閉じたら、ライセンスの確認をやり直す。
//   ・キャッシュがある（サーバーの応答が1秒に間に合わず、後から届いた）… その場で再適用する
//   ・キャッシュが無い（サーバーに届かなかった）                       … リロードする
// ※barkoder の SDK は、ライセンスのサーバーへの問い合わせが終わったあと（通信の失敗・応答あり）は、
// 　initialize() を呼び直しても問い合わせ直さないため、その場の再適用では直らない
// 　（2026-10-27 Dev で、遮断を解いたあとの再適用で barkoder.com への通信が0件だったことを確認）。
// 　応答待ちのあいだに呼び直した場合だけ、もう一度問い合わせる（D-38）。
// 　読み込み直せば、新しいページとしてもう一度問い合わせる。
// ※iAEON モードでは、リロードしても「保存されている商品情報があります」から買い物を続けられる

//スキャン画面がライセンスのエラーを見分けるための目印
export const BARKODER_LICENSE_ERROR_CODE = "BARKODER_LICENSE";

const LICENSE_STATE_UNKNOWN = "unknown";
const LICENSE_STATE_CHECKING = "checking";
const LICENSE_STATE_OK = "ok";
const LICENSE_STATE_NG = "ng";

//初期化の完了から自動で再試行する時刻（ms）
//※3秒後は D-30 からある保険で、状態にかかわらず呼ぶ。8秒・15秒は準備できていないときだけ。
//　スタート画面で初期化が始まるため、利用者がスキャン画面に入る頃にはたいてい終わっている
const LICENSE_RETRY_SCHEDULE = [LICENSE_REAPPLY_DELAY, 8000, 15000];
//利用者の再試行がこの回数続けて失敗したら、文言を「時間をおいて」に変える
const LICENSE_USER_RETRY_LIMIT = 3;

let licenseState = LICENSE_STATE_UNKNOWN;
//エラーを表示中か（閉じたあとの resumeDecoding() で再試行するため
let licenseErrorShown = false;
//利用者の再試行が続けて失敗した回数
let licenseUserFailures = 0;
//スキャン画面に入ってから resumeDecoding() が呼ばれたか
//※入った直後の resumeDecoding() でエラーを出し、2回目以降（エラーを閉じたあと）で再試行するため
let licenseScreenReady = false;
//実行中の再試行（自動と利用者の再試行が重ならないようにする
let licenseRetryPromise = null;
//準備できていない間、読み取りを止めているか
//※準備できていなければ読み取り値はマスクされるだけで使えない。読み取りを続けるとメインスレッドが詰まり、
//　SDK の「20ms待つ×50回」の待ちが実時間で大幅に延びて、ライセンスの確認に8〜18秒かかった（2026-10-27 Dev で確認）
let licenseDecodeHeld = false;
//リロードを始めたか（画面を離れたときと閉じたときで二重に行わない
let licenseReloading = false;

//リロードした回数（リロードをまたいで数えるため sessionStorage に置く
const LICENSE_RELOAD_COUNT_KEY = "regigo_barkoder_license_reload";
//閉じたときの音が途中で切れないよう、少し待ってからリロードする（Setting.jsx と同じ考え方
const LICENSE_RELOAD_DELAY = 400;

licenseDiag.state = licenseState;

//1回の確認の結果を判定する
//  ok      … 準備できた
//  late    … サーバーは応答したが1秒に間に合わなかった（応答はキャッシュされるので次の確認で適用できる）
//  none    … サーバーが応答しなかった
//  unknown … 判定できない
function judgeLicenseCheck(rec) {
	if (!rec || !rec.watching || rec.error) return "unknown";
	if (rec.BarkoderSDK_callback3D !== null) return "ok";
	if (rec.serverWaitStartedAt === null) return "unknown";
	if (rec.BarkoderSDK_callback2D === null) return "none";
	return rec.BarkoderSDK_callback2D - rec.serverWaitStartedAt > LICENSE_SERVER_WAIT_LIMIT ? "late" : "ok";
}

function setLicenseState(next, reason) {
	if (licenseState !== next) {
		console.log("【barkoder】ライセンスの準備: " + licenseState + " → " + next + "（" + reason + "）");
		licenseState = next;
		licenseDiag.state = next;
	}
	if (next === LICENSE_STATE_OK) {
		licenseErrorShown = false;
		licenseUserFailures = 0;
		resetLicenseReloadCount();
		//止めていた読み取りを再開する（スキャン画面を表示中のときだけ
		if (licenseDecodeHeld) {
			licenseDecodeHeld = false;
			if (scanHandler) resumeDecodingInternal();
		}
	}
	//スキャン画面を表示中なら、その場でエラーを出す（入る前ならスキャン画面に入った直後に出す
	if (next === LICENSE_STATE_NG && licenseScreenReady) {
		notifyLicenseNotReady();
	}
}

//確認の結果を状態に反映する
//※isFinal … これで準備できなければ諦める（自動の再試行を使い切った／利用者の再試行）
function applyLicenseCheck(rec, isFinal, reason) {
	const result = judgeLicenseCheck(rec);
	if (result === "unknown") return result;
	if (result === "ok") {
		setLicenseState(LICENSE_STATE_OK, reason);
	} else {
		setLicenseState(isFinal ? LICENSE_STATE_NG : LICENSE_STATE_CHECKING, reason);
	}
	return result;
}

//ライセンスのエラー（スキャン画面の onScanError で利用者向けの文言に置き換える
function createLicenseError(detail) {
	const error = new Error("barkoder license not ready / " + detail);
	error.code = BARKODER_LICENSE_ERROR_CODE;
	error.retryOver = licenseUserFailures + getLicenseReloadCount() >= LICENSE_USER_RETRY_LIMIT;
	return error;
}

function notifyLicenseNotReady() {
	if (!errorHandler || licenseErrorShown) return false;
	licenseErrorShown = true;

	const last = licenseDiag.reapply || licenseDiag.init;
	console.log("【barkoder】ライセンスの準備ができていないためエラーを通知する（" + describeUnlicensedContext() + "・その場の再試行の失敗" + licenseUserFailures + "回・リロード" + getLicenseReloadCount() + "回）");
	errorHandler(createLicenseError((last && last.path) || "経路は不明"));
	return true;
}

//ライセンスの確認をやり直す
//※kind … "自動"（初期化からの再試行） / "利用者"（エラーを閉じた・画面を離れた）
function runLicenseRetry(kind, isFinal) {
	if (licenseRetryPromise) return licenseRetryPromise;

	licenseRetryPromise = (async () => {
		try {
			let result = await reapplyAndJudge();

			//応答が1秒に間に合わなかっただけなら、キャッシュされた応答で適用できるのでもう一度だけ呼ぶ
			if (result === "late") {
				result = await reapplyAndJudge();
			}

			if (result === "unknown") return result;
			if (result === "ok") {
				setLicenseState(LICENSE_STATE_OK, kind + "の再試行");
				return result;
			}
			if (kind === "利用者") licenseUserFailures += 1;
			//※すでに ng（マスクされた値を検知した等）なら、失敗した再試行で checking に戻さない
			//　（戻すと、準備できていないのに「確認中」として扱われる。2026-10-27 クイックレジの Dev で確認）
			const failed = isFinal || licenseState === LICENSE_STATE_NG;
			setLicenseState(failed ? LICENSE_STATE_NG : LICENSE_STATE_CHECKING, kind + "の再試行");
			return result;
		} finally {
			licenseRetryPromise = null;
		}
	})();

	return licenseRetryPromise;
}

//再適用して、その確認の結果を判定する
//※再適用のあとのデコーダ設定の当て直しなどで失敗しても、ライセンスの確認そのものは終わっているので記録で判定する。
//　確認まで進まなかった（SDK の読み込みに失敗した等）ときは、記録が古いままなので判定できない扱いにする
async function reapplyAndJudge() {
	const calledAt = Date.now();
	await reapplyLicense({ force: true });
	const rec = licenseDiag.reapply;
	if (!rec || rec.ms === undefined) return "unknown";
	//※すでに実行中だった再適用に合流した場合は、その確認の記録になる（同じ時点の状態なので判定に使ってよい
	if (rec.startedAt < calledAt - BARKODER_INIT_TIMEOUT) return "unknown";
	return judgeLicenseCheck(rec);
}

//エラーを閉じた（または表示したまま画面を離れた）あとの、確認のやり直し
function retryLicenseAfterError() {
	licenseErrorShown = false;

	//応答が後から届いてキャッシュされていれば、その場の再適用で直る
	if (readLicenseCache().exists) {
		runLicenseRetry("利用者", true);
		return;
	}

	//サーバーに届かなかった場合は、リロードしないと問い合わせ直さない（節の説明を参照
	reloadForLicense();
}

function reloadForLicense() {
	if (licenseReloading) return;
	licenseReloading = true;

	const count = getLicenseReloadCount() + 1;
	try {
		window.sessionStorage.setItem(LICENSE_RELOAD_COUNT_KEY, String(count));
	} catch (e) {}
	console.log("【barkoder】ライセンスの確認をやり直すためリロードする（" + count + "回目）");

	setTimeout(() => {
		//time 以外のURLパラメータは保持する（Setting.jsx の reloadForScanSdk と同じ
		//※App.js の初期化は「time が無ければ付け直してリダイレクト」する作り
		try {
			const url = new URL(window.location.href);
			url.searchParams.delete("time");
			window.location.href = url.href;
		} catch (e) {
			window.location.reload();
		}
	}, LICENSE_RELOAD_DELAY);
}

function getLicenseReloadCount() {
	try {
		return Number(window.sessionStorage.getItem(LICENSE_RELOAD_COUNT_KEY) || 0);
	} catch (e) {
		return 0;
	}
}

function resetLicenseReloadCount() {
	try {
		window.sessionStorage.removeItem(LICENSE_RELOAD_COUNT_KEY);
	} catch (e) {}
}

//準備できるまで読み取りを止める（理由は licenseDecodeHeld のコメント参照
function holdDecodingForLicense() {
	licenseDecodeHeld = true;
	decodingActive = false;
	applyPauseDecoding(barkoder, true);
}

//初期化の完了後に、自動の再試行を予約する
function scheduleLicenseRetries() {
	LICENSE_RETRY_SCHEDULE.forEach((delay, index) => {
		const isLast = index === LICENSE_RETRY_SCHEDULE.length - 1;
		setTimeout(() => {
			//3秒後（index 0）は D-30 からある保険なので、判定できない環境でも従来どおり呼ぶ
			if (index > 0 && (licenseState === LICENSE_STATE_OK || licenseState === LICENSE_STATE_UNKNOWN)) return;
			runLicenseRetry("自動", isLast);
		}, delay);
	});
}

//カメラ描画先のID
//※barkoder内部の Decoder.start() が document.getElementById('barkoder-container') を
//　直接参照しているため、このIDは変更できない（商品スキャン・QRスキャンで共通）
export const BARKODER_CONTAINER_ID = "barkoder-container";

//----------------------------------------
//ライセンスキー取得（ドメイン別
//----------------------------------------

export function getBarkoderLicenseKey() {
	if (typeof window === "undefined") {
		return process.env.REACT_APP_BARKODER_LICENSE_DEV;
	}

	const domain = window.location.host;
	console.log("【barkoder】domain: " + domain);

	if (domain === "miniapp.regigo.jp") {
		return process.env.REACT_APP_BARKODER_LICENSE_PRO;
	} else if (domain === "miniapp.irs-test.jp") {
		return process.env.REACT_APP_BARKODER_LICENSE_DEV;
	} else if (domain === "test.000080.co.jp") {
		return process.env.REACT_APP_BARKODER_LICENSE_NAVY;
	} else if (domain === "regigoapp.01up.tv") {
		return process.env.REACT_APP_BARKODER_LICENSE_1UP;
	} else if (domain === "regigoapp.testdrives.dev") {
		return process.env.REACT_APP_BARKODER_LICENSE_TESTDRIVE;
	}

	// local
	return process.env.REACT_APP_BARKODER_LICENSE_LOCAL;
}

//----------------------------------------
//SDK本体（barkoder.js）の読み込み
//----------------------------------------

function loadBarkoderScript() {
	if (typeof window === "undefined") {
		return Promise.reject(new Error("barkoder: window is undefined"));
	}
	if (window.BarkoderSDK) {
		return Promise.resolve(window.BarkoderSDK);
	}
	if (scriptPromise) {
		return scriptPromise;
	}

	console.log("【barkoder】load " + BARKODER_JS_PATH);

	scriptPromise = new Promise((resolve, reject) => {
		let timer = null;

		const finish = (instance, error) => {
			if (timer) {
				clearInterval(timer);
				timer = null;
			}
			if (error) {
				//次回リトライできるように解放
				scriptPromise = null;
				reject(error);
			} else {
				resolve(instance);
			}
		};

		let script = document.querySelector("script[data-barkoder-sdk]");
		if (!script) {
			script = document.createElement("script");
			script.src = BARKODER_JS_PATH;
			script.async = true;
			script.setAttribute("data-barkoder-sdk", "true");
			script.onerror = () => finish(null, new Error("barkoder: " + BARKODER_JS_PATH + " load error"));
			document.head.appendChild(script);
		}

		//UMDは async IIFE のため、script読み込み完了後に window.BarkoderSDK が生成される
		const started = Date.now();
		timer = setInterval(() => {
			if (window.BarkoderSDK) {
				finish(window.BarkoderSDK, null);
			} else if (Date.now() - started > BARKODER_JS_TIMEOUT) {
				finish(null, new Error("barkoder: " + BARKODER_JS_PATH + " load timeout"));
			}
		}, 50);
	});

	return scriptPromise;
}


//----------------------------------------
//初期化（多重実行防止
//----------------------------------------

export async function initBarkoder() {
	if (barkoder) {
		return barkoder;
	}
	if (initPromise) {
		return initPromise;
	}

	console.log("【barkoder】initialize");

	initPromise = (async () => {
		try {
			//SDK本体は選択時のみ読み込む（初期表示のバンドルサイズ対策
			const BarkoderSDK = await loadBarkoderScript();

			//initialize() は1回だけ呼び、以降は同じPromiseを待つ。
			//※待ち先の Connector.moduleLoaded はSDK内のモジュール変数なので、
			//　1回目が終わらない状態で呼び直しても同じ場所で待つだけで意味がない。
			//　呼ぶたびに待ち時間が積み上がるのを避ける
			if (!sdkInitPromise) {
				const licenseKey = getBarkoderLicenseKey() || "";
				sdkInitStartedAt = Date.now();
				//ライセンス確認の診断（ログのみ。詳細は「ライセンス確認の診断」の節を参照
				const licenseCheck = beginLicenseCheck("初期化");
				licenseDiag.init = licenseCheck;
				sdkInitPromise = BarkoderSDK.initialize(licenseKey);
				sdkInitPromise.then(
					() => {
						endLicenseCheck(licenseCheck);
						//準備の状態に反映（できていなければ自動の再試行に任せる
						applyLicenseCheck(licenseCheck, false, "初期化");
					},
					(e) => endLicenseCheck(licenseCheck, e)
				);
				//未処理のrejectにならないようにしておく（待ち直しは下のwithTimeoutで行う
				sdkInitPromise.catch(() => {});
			}

			//1回目の呼び出しからの経過で打ち切る（呼び直しでも合計の待ちは変わらない
			const rest = Math.max(1000, BARKODER_INIT_TIMEOUT - (Date.now() - sdkInitStartedAt));

			//※wasmが読めないと完了しないため必ずタイムアウトを掛ける（上の定数のコメントを参照
			const instance = await withTimeout(sdkInitPromise, rest, "barkoder initialize");

			//共通設定
			if (instance.setBeepOnSuccessEnabled) {
				instance.setBeepOnSuccessEnabled(false);
			}
			//カメラ解像度：設定画面／envで切り替える（既定はFullHD＝従来どおり
			//CameraResolution: HD = 0, FHD = 1
			if (instance.setCameraResolution) {
				const constants = instance.constants?.CameraResolution;
				const useHd = getCameraResolution() === CAMERA_RESOLUTION_HD;
				const fallback = useHd ? 0 : 1;
				const value = useHd ? constants?.HD : constants?.FHD;
				instance.setCameraResolution(typeof value === "number" ? value : fallback);
				console.log("【barkoder】camera resolution: " + getCameraResolution());
			}
			//1秒あたりのデコード回数
			//※barkoderはメインスレッドで「全解像度の getImageData → wasmデコード」を
			//　setInterval(1000/dps) ごとに同期実行する。1回あたりの処理が重いと
			//　メインスレッドが飽和し、プレビューの合成が止まってコマ落ちする。
			//　FHDでは15回/秒でも実機（1080x2340）で毎秒4〜5回のコマ落ちが出たため10回/秒。
			//　HDは1回あたりが約2.25分の1になるぶん15回/秒まで増やせる（そのぶん取得が速い）。
			//　値の根拠は cameraResolutionSetting.js を参照
			if (instance.setDpsLimit) {
				const dps = getBarkoderDpsLimit();
				instance.setDpsLimit(dps);
				console.log("【barkoder】dpsLimit: " + dps);
			}

			//結果画像は使わないので無効化（1フレームぶんのImageData保持を避ける
			if (instance.setImageResultEnabled) instance.setImageResultEnabled(false);
			if (instance.setBarcodeThumbnailOnResultEnabled) instance.setBarcodeThumbnailOnResultEnabled(false);

			//スキャナーの自動停止を無効化
			//※barkoderは既定でカメラ起動から60秒後に stopPreview() を実行し、
			//　プレビューが黒画面のまま止まってしまう（config.timeout の単位は秒。0で無効
			if (instance.setScannerTimeout) instance.setScannerTimeout(0);

			//万一タイムアウトした場合の保険（スキャン画面表示中なら再開する
			if (typeof instance.addEventListener === "function") {
				try {
					instance.addEventListener("scannerTimeout", () => {
						console.log("【barkoder】scannerTimeout → restart scanner");
						cameraOn = false;
						if (scanHandler) {
							startCamera().then((started) => {
								if (started) resumeDecodingInternal();
							});
						}
					});
				} catch (e) {
					console.log("【barkoder】addEventListener error: " + e);
				}
			}

			//barkoder標準UI（フラッシュ・ズーム・閉じる・カメラ切替）を非表示
			//※ボタンのDOMは初回スキャン開始時に1度だけ生成されるため、必ずstart前に設定する
			if (instance.setFlashEnabled) instance.setFlashEnabled(false);
			if (instance.setZoomEnabled) instance.setZoomEnabled(false);
			if (instance.setCloseEnabled) instance.setCloseEnabled(false);
			if (instance.setCameraPickerEnabled) instance.setCameraPickerEnabled(false);

			//検出時にバーコード位置へ表示される枠（既定：赤 #ff3347 を400ms）を非表示
			//※scandit もオーバーレイ（追跡枠）を使っていないため挙動を合わせる
			if (instance.setLocationInPreviewEnabled) instance.setLocationInPreviewEnabled(false);

			//走査範囲（ROI）の枠と外側の暗転を非表示
			//※上記の検出枠とは別のオーバーレイ。既定は roiVisible:true で
			//　赤枠（#ff3347）＋外側 rgba(0,0,0,0.5) が描画される。
			//　アプリ側のスキャン枠（scanCover）を使うため不要
			if (instance.setRegionOfInterestVisible) instance.setRegionOfInterestVisible(false);

			barkoder = instance;
			console.log("【barkoder】initialize done");

			//ライセンス確認のサーバー応答が1秒に間に合わなかった場合の保険。
			//応答が届いたころに呼び直すと適用される（詳細は reapplyLicense のコメント参照
			//※初期化を待たせたくないので await しない。
			//　3秒後の1回に加え、準備できていなければ8秒・15秒にも試す（「ライセンスの準備の状態」の節を参照
			scheduleLicenseRetries();
			return barkoder;
		} finally {
			initPromise = null;
		}
	})();

	return initPromise;
}

//----------------------------------------
//インスタンス取得（未初期化なら初期化
//----------------------------------------

export async function getBarkoder() {
	if (barkoder) {
		return barkoder;
	}
	try {
		const instance = await initBarkoder();
		lastInitError = null;
		return instance;
	} catch (e) {
		console.log("【barkoder】getBarkoder error: " + e);
		lastInitError = e;
		return null;
	}
}

//----------------------------------------
//直近の初期化失敗の理由
//----------------------------------------
// スキャン画面のエラー表示に載せる。
// 実機（WebView）ではログを取れないことがあり、
// 「カメラが起動しない」だけでは原因を切り分けられないため。

export function getLastInitError() {
	if (!lastInitError) return null;
	return String(lastInitError.message || lastInitError);
}

//----------------------------------------
//背面カメラの明示指定
//----------------------------------------
// barkoder は cameraId 未指定なら facingMode:'environment' を要求するが、
// scandit（Camera.atPosition(WorldFacing)）や scanbot（preferredCamera）に合わせて
// 背面のメインカメラを明示的に選択する。
// 判別できない場合は cameraId を空にして SDK 既定（facingMode指定）へフォールバックする。

async function applyBackCamera(instance) {
	if (typeof instance.getCameras !== "function" || typeof instance.setCameraId !== "function") {
		return;
	}

	//特定済みならそのまま使う
	//※getCameras() は内部で getUserMedia({video:true}) を実行して一時的にカメラを掴むため、
	//　起動のたびに呼ぶと本番の getUserMedia と競合する
	if (backCameraId !== null) {
		instance.setCameraId(backCameraId);
		return;
	}

	try {
		const cameras = await instance.getCameras();
		if (!Array.isArray(cameras) || cameras.length === 0) {
			return;
		}

		//背面カメラ（ラベルは端末・言語によって異なる
		const isBack = (label) => /back|rear|environment|背面|外側/i.test(label || "");
		//超広角・望遠・マクロ・深度は避ける（"広角"は主カメラ名に含まれるため除外しない
		const isSubLens = (label) => /ultra|超広角|tele|望遠|macro|マクロ|depth|深度/i.test(label || "");

		const backCameras = cameras.filter((camera) => isBack(camera.label));
		if (backCameras.length === 0) {
			//判別できないときは既定の facingMode:'environment' に任せる
			//（前回選択がlocalStorageに残っていても使われないようにクリアする
			backCameraId = "";
			instance.setCameraId("");
			console.log("【barkoder】back camera not found → facingMode指定にフォールバック");
			return;
		}

		const mainCameras = backCameras.filter((camera) => !isSubLens(camera.label));
		const target = (mainCameras.length > 0 ? mainCameras : backCameras)[0];

		if (target && target.id) {
			backCameraId = target.id;
			instance.setCameraId(target.id);
			console.log("【barkoder】back camera: " + target.label);
		}
	} catch (e) {
		console.log("【barkoder】applyBackCamera error: " + e);
	}
}


//----------------------------------------
//カメラ起動（常駐コンテナへ・デコードは停止状態
//----------------------------------------
// スタート画面・スキャンボタン押下時に呼び出してカメラを起動しておく。
// スキャン画面ではこのカメラを使い回すため、初回表示が速くなる。

export function startCamera() {
	return enqueue(async () => {
		//起動済みでもストリームが死んでいる場合（バックグラウンド復帰後など）は作り直す
		//※起動直後はまだストリームが上がっていないことがあるため、即断せず少し待つ
		if (cameraOn) {
			if (await waitForCameraLive()) {
				return true;
			}
			console.log("【barkoder】camera is not live → restart");
			await releaseCameraInternal();
		}

		try {
			const instance = await getBarkoder();
			if (!instance) {
				return false;
			}

			//常駐コンテナ（body直下・非表示）を用意
			const host = createHost(BARKODER_CONTAINER_ID);

			//スキャナー起動時にコンテナが退避状態（160x120）だったかを覚えておく。
			//※SDKはこのサイズでプレビューを組み立てるため、あとでスキャン画面の
			//　スロット（全画面サイズ）へ付け替えても映像が小さいまま残る。
			//　スタート画面でカメラを起動する構成（D-05）では必ずこの状態になるので、
			//　スキャン画面が表示された時点で組み直す（refreshLayout）
			startedWhileParked = !host.parentElement || host.parentElement === document.body;

			applyDecoderSettings(instance);

			instance.setContainerOptions({
				containerId: BARKODER_CONTAINER_ID,
				width: host.offsetWidth || 1,
				height: host.offsetHeight || 1,
				//コンテナのサイズはCSSで制御する（付け替え先のサイズに追従させる
				overrideSize: false,
				useFallbackSize: false,
				useDefaultUI: false,
			});

			instance.setContinuous(true);

			//背面カメラを明示指定（startScannerより前に設定する必要がある
			await applyBackCamera(instance);

			//スキャン画面に入るまではデコードを止めておく（カメラは起動したまま
			applyPauseDecoding(instance, true);
			decodingActive = false;

			//SDK内部の runningState.active が立ったままだと start() は何もせずに返る。
			//その場合 getUserMedia が走らず、プレビューが空のまま「起動成功」になる。
			//開始前に必ず停止させておく（非アクティブなら stopPreview() は内部で何もしない
			await safeStopScanner(instance);

			console.log("【barkoder】startScanner (camera on)");

			instance.startScanner((result) => {
				onScannerResult(result);
			});

			cameraOn = true;

			//カメラの能力をログへ（AF対策の切り分け用。ストリーム確立を待つのでawaitしない
			logCameraCapabilities(hostElement(), "barkoder");

			//フォーカスの固定（設定でAUTO以外を選んでいる場合のみ。同じくawaitしない
			applyCameraFocus(hostElement(), "barkoder", getCameraFocus());

			//バックグラウンド復帰の監視を開始（初回のみ
			registerVisibilityHandlers();

			//プレビュー停止の監視を開始
			startPreviewWatchdog();

			return true;
		} catch (e) {
			console.log("【barkoder】startCamera error: " + e);
			return false;
		}
	});
}

//----------------------------------------
//カメラ停止（完全停止
//----------------------------------------
// scandit と同じく、スキャン画面から離れたらカメラを閉じる

export function releaseCamera() {
	return enqueue(releaseCameraInternal);
}

//直列化キューの内部から呼ぶ用（enqueueしない
async function releaseCameraInternal() {
	if (!cameraOn) {
		return;
	}

	cameraOn = false;
	stopPreviewWatchdog();
	applyPauseDecoding(barkoder, true);
	decodingActive = false;
	//カメラを起動し直したら表示サイズの是正もやり直す
	layoutFixTried = false;
	startedWhileParked = false;

	if (barkoder) {
		await safeStopScanner(barkoder);
	}

	hardStopMediaStream(hostElement(), null);
	console.log("【barkoder】camera released");
}

//----------------------------------------
//カメラのストリームが生きているか
//----------------------------------------

function isCameraLive() {
	const video = hostElement()?.querySelector("video");
	const stream = video?.srcObject;
	if (!stream || typeof stream.getVideoTracks !== "function") {
		return false;
	}
	const track = stream.getVideoTracks()[0];
	return !!track && track.readyState === "live";
}

//----------------------------------------
//ストリームが上がるのを待つ
//----------------------------------------
// barkoder の startScanner() は「ストリームが上がる前に」返る。
// 起動直後に isCameraLive() を評価すると false になり、
// 初期化中のスキャナーを「死んでいる」と誤判断して破棄してしまう。
// その場合 SDK内部のDOM参照がまだ無いため stopScanner が
// TypeError（removeEventListener of null）になり、スキャンできなくなる。
// 低スペック端末ではストリーム確立が遅く、この誤判断を踏みやすい。

const CAMERA_LIVE_TIMEOUT = 2000;
const CAMERA_LIVE_INTERVAL = 100;

async function waitForCameraLive() {
	const limit = Date.now() + CAMERA_LIVE_TIMEOUT;

	while (true) {
		if (isCameraLive()) return true;
		if (Date.now() >= limit) return false;
		await new Promise((resolve) => setTimeout(resolve, CAMERA_LIVE_INTERVAL));
	}
}

//----------------------------------------
//バックグラウンド復帰時のカメラ再開
//----------------------------------------
// 他アプリへの切替やスリープでカメラのトラックが停止し、
// 復帰してもプレビューが黒いままになるため、可視状態に戻ったら復旧させる。

function registerVisibilityHandlers() {
	if (visibilityHandlersRegistered || typeof document === "undefined") {
		return;
	}
	visibilityHandlersRegistered = true;

	//「本来カメラが動いているべき状態か」はSDKファサードが保持しているため、
	//非表示イベントの発火有無に依存せず、表示に戻ったタイミングで判定する
	const onVisible = () => {
		if (document.hidden) return;
		if (!cameraOn) return;
		resumeCamera();
	};

	document.addEventListener("visibilitychange", onVisible);
	//iOS（bfcache復帰）・他アプリからの復帰対策
	window.addEventListener("pageshow", onVisible);
	window.addEventListener("focus", onVisible);
}

async function resumeCamera() {
	if (resuming) return;
	resuming = true;

	//復帰前の状態（停止処理で解除されるため先に控える
	const wantDecoding = decodingActive;

	try {
		if (isCameraLive()) {
			//ストリームが生きていれば再生し直すだけ（中断で一時停止するため
			playVideos(hostElement());
		} else {
			console.log("【barkoder】visibility resume → restart scanner");
			const ok = await restartAndRestore();
			if (!ok) return;
		}

		//スキャン画面表示中かつ復帰前にデコード中だった場合のみ再開する
		if (scanHandler && wantDecoding) {
			resumeDecodingInternal();
		}
	} catch (e) {
		console.log("【barkoder】resumeCamera error: " + e);
	} finally {
		resuming = false;
	}
}

//----------------------------------------
//プレビュー停止（フリーズ）の検知と復旧
//----------------------------------------
// barkoderはメインスレッドでデコードするため、負荷が高いとプレビューが止まり、
// そのまま黒画面になることがある。videoの再生位置が進んでいるかで検知する。

function startPreviewWatchdog() {
	stopPreviewWatchdog();

	lastVideoTime = -1;
	stallCount = 0;

	watchdogTimer = setInterval(() => {
		//スキャン画面を表示していない／非表示中は監視しない
		if (!cameraOn || !scanHandler || document.hidden) {
			lastVideoTime = -1;
			stallCount = 0;
			return;
		}

		const video = hostElement()?.querySelector("video");
		if (!video) return;

		const currentTime = video.currentTime;

		if (lastVideoTime >= 0 && currentTime === lastVideoTime) {
			stallCount++;
			if (stallCount >= WATCHDOG_STALL_LIMIT) {
				stallCount = 0;
				recoverFrozenPreview();
			}
		} else {
			stallCount = 0;
		}

		lastVideoTime = currentTime;
	}, WATCHDOG_INTERVAL);
}

function stopPreviewWatchdog() {
	if (watchdogTimer) {
		clearInterval(watchdogTimer);
		watchdogTimer = null;
	}
}

async function recoverFrozenPreview() {
	if (resuming) return;
	resuming = true;

	const wantDecoding = decodingActive;

	try {
		console.log("【barkoder】preview frozen → restart scanner");
		const ok = await restartAndRestore();
		if (!ok) return;

		if (scanHandler && wantDecoding) {
			resumeDecodingInternal();
		}
	} catch (e) {
		console.log("【barkoder】recoverFrozenPreview error: " + e);
	} finally {
		resuming = false;
	}
}

//スキャナーを開始し直して、プレビューが表示に戻るまで面倒を見る
async function restartAndRestore() {
	const started = await restartScanner();
	if (!started) return false;

	await ensurePreviewVisible();
	return true;
}

//----------------------------------------
//スキャナーの再開（バックグラウンド復帰用
//----------------------------------------
// barkoder はバックグラウンド移行時に自前の visibilitychange で stopScanner() を呼ぶため、
// 復帰時は「停止済みのスキャナーを開始し直す」だけでよい。
// ・srcObject を外す操作はSDKの参照を崩すため行わない（stopVideoElement のコメント参照）
// ・getCameras() は内部で getUserMedia({video:true}) を実行して一時的にカメラを掴むため、
// 　直後の本番 getUserMedia と競合する。cameraId は前回の指定が残るので復帰時は再取得しない

function restartScanner() {
	return enqueue(async () => {
		const instance = barkoder;
		if (!instance) {
			return false;
		}

		try {
			//停止済みなら内部で何もしない
			await safeStopScanner(instance);

			instance.setContinuous(true);
			applyPauseDecoding(instance, true);
			decodingActive = false;

			console.log("【barkoder】restartScanner");

			instance.startScanner((result) => {
				onScannerResult(result);
			});

			cameraOn = true;
			startPreviewWatchdog();

			//組み直すとトラックが作り直され、フォーカスの指定が消えるため当て直す
			applyCameraFocus(hostElement(), "barkoder", getCameraFocus());

			return true;
		} catch (e) {
			console.log("【barkoder】restartScanner error: " + e);
			return false;
		}
	});
}

//----------------------------------------
//プレビューの表示復帰
//----------------------------------------
// barkoder は停止時に cameraPreview を visibility:hidden にし、
// video の loadeddata を受けて showElements() で表示へ戻す。
// 復帰時にこのイベントを取りこぼすと黒画面のままになるため、明示的に戻す。

async function ensurePreviewVisible() {
	//映像が届く前に表示へ戻すと、iOSでは空のvideoにネイティブの再生ボタンが一瞬見えるため、
	//フレームが来てから（またはタイムアウト後に保険として）表示へ戻す
	let ready = false;

	for (let i = 0; i < 20; i++) {
		await new Promise((resolve) => setTimeout(resolve, 150));

		playVideos(hostElement());

		if (isPreviewReady()) {
			ready = true;
			break;
		}
	}

	showPreviewElement();

	//復帰できたかどうかの診断ログ
	const video = hostElement()?.querySelector("video");
	const track = video?.srcObject?.getVideoTracks?.()[0];
	console.log(
		"【barkoder】resume state: ready=" + ready +
		" live=" + isCameraLive() +
		" readyState=" + video?.readyState +
		" videoWidth=" + video?.videoWidth +
		" paused=" + video?.paused +
		" track=" + track?.readyState
	);
}

//プレビューに映像が届いているか
function isPreviewReady() {
	const video = hostElement()?.querySelector("video");
	if (!video || video.readyState < 3 || video.videoWidth === 0) {
		return false;
	}
	return isCameraLive();
}

//barkoderが停止時に隠したプレビューを表示へ戻す
function showPreviewElement() {
	const preview = document.getElementById("cameraPreview");
	if (preview && preview.style.visibility === "hidden") {
		preview.style.visibility = "visible";
	}
}


//----------------------------------------
//スキャン画面への付け替え
//----------------------------------------

export function connectToElement(slot) {
	attachHost(hostElement(), slot);
}

export function detachFromElement(slot) {
	detachHost(hostElement(), slot);
}

//----------------------------------------
//プレビュー表示サイズの是正
//----------------------------------------
// 映像が小さいまま表示される端末がある（iPhone 13 / iOS 17.6.1 で発生）。
// SDKはコンテナのサイズと映像の縦横比から表示サイズを決めるが、その計算が
// 映像到達前に走ると縦横比が求まらず、videoにスタイルが当たらないまま残る。
// （詳細は scanHost.js の refreshHostLayout を参照）
//
// リサイズ通知では是正されず、実機ではスリープ復帰
// （＝スキャナーの再起動）でのみ全画面に戻った。そのため、コンテナに対して
// 明らかに小さい場合はスキャナーを再起動する。
// 再起動は映像が途切れるので、カメラ起動ごとに1回までとする。

let layoutFixTried = false;
//スキャナー起動時にコンテナが退避状態だったか（startCamera で設定）
let startedWhileParked = false;

//SDKは短辺をコンテナに合わせて長辺をはみ出させる（クロップ表示）ので、
//幅・高さのどちらかがコンテナを満たしていれば正常とみなす
//※zoom の影響を受けないよう、どちらも getBoundingClientRect で比較する
function isPreviewFitted() {
	const host = hostElement();
	const video = host?.querySelector("video");
	if (!host || !video) return true;

	const hostRect = host.getBoundingClientRect();
	if (hostRect.width < 2 || hostRect.height < 2) return true;

	const videoRect = video.getBoundingClientRect();

	return (
		videoRect.width >= hostRect.width * 0.98 ||
		videoRect.height >= hostRect.height * 0.98
	);
}

//組み直しの前後でデコード状態を引き継ぐ
//※restartScanner() は内部で applyPauseDecoding(true) / decodingActive = false を行う。
//　resumeCamera() / recoverFrozenPreview() と同じく、呼び出し側で戻す必要がある
function restoreDecoding(wantDecoding) {
	if (!scanHandler || !wantDecoding) return;

	resumeDecodingInternal();

	//※setPauseDecoding は runningState.paused を書くだけだが、startScanner() は
	//　SDK内部で非同期に初期化される。組み直し直後に false を書いても、
	//　あとから走る初期化処理に上書きされることがある（Androidで発生。iOSでは出ない）。
	//　少し置いて2回だけ指定し直す。
	//　※検知後は アプリ側の pauseDecoding() で decodingActive が false になるため、
	//　　SDKが検知直後にかける一時停止（重複読み取り防止）を打ち消すことはない
	setTimeout(reassertDecoding, 300);
	setTimeout(reassertDecoding, 900);
}

function reassertDecoding() {
	if (!scanHandler || !decodingActive) return;
	applyPauseDecoding(barkoder, false);
}

export async function refreshLayout() {
	try {
		refreshHostLayout(hostElement());

		if (layoutFixTried) return;
		if (!scanHandler || !cameraOn) return;

		//組み直すとデコードが止まるため、現在の状態を退避しておく
		const wantDecoding = decodingActive;

		//退避状態で起動していた場合は、サイズ判定を待たずに組み直す。
		//このときSDKが持っているジオメトリは 160x120 基準で確実に誤っているため
		if (startedWhileParked) {
			layoutFixTried = true;
			startedWhileParked = false;
			console.log("【barkoder】started while parked → restart scanner");

			await restartAndRestore();
			refreshHostLayout(hostElement());

			//※restartScanner はデコードを停止した状態で終わるため、必ず再開する。
			//　これを忘れると「カメラは映るがスキャンできない」状態になる
			restoreDecoding(wantDecoding);
			return;
		}

		//保険：上記に当てはまらなくても表示が小さい場合は組み直す
		//スタイルの反映を待ってから判定する
		await new Promise((resolve) => setTimeout(resolve, 400));

		if (!scanHandler || !cameraOn) return;
		if (isPreviewFitted()) return;

		layoutFixTried = true;
		console.log("【barkoder】preview size mismatch → restart scanner");

		await restartAndRestore();
		refreshHostLayout(hostElement());

		//※restartScanner はデコードを停止した状態で終わるため、必ず再開する
		restoreDecoding(wantDecoding);
	} catch (e) {
		console.log("【barkoder】refreshLayout error: " + e);
	}
}

function hostElement() {
	return document.getElementById(BARKODER_CONTAINER_ID);
}

//----------------------------------------
//スキャン結果ハンドラ（表示中の画面に振り分け
//----------------------------------------
// scanMode: "barcode"（商品スキャン） / "qr"（QRスキャン）

export function setScanHandler(mode, handler, onError) {
	scanMode = mode;
	scanHandler = handler;
	errorHandler = onError;
	//ライセンスのエラーは、この画面の最初の resumeDecoding() で出す
	licenseScreenReady = false;
}

//----------------------------------------
//スキャン画面の解放（デコード停止＋コンテナ退避
//----------------------------------------
// 画面遷移アニメーション中は「次の画面のマウント → 前の画面のアンマウント」の順になるため、
// 既に次の画面が使用中の場合は何もしない（次の画面のデコードを止めない）

export function releaseScreen(handler, slot) {
	//既に次の画面が使用中なら何もしない（解放しなかったことを呼び出し元へ返す
	if (scanHandler !== handler) return false;

	pauseDecoding();
	scanHandler = null;
	scanMode = null;
	errorHandler = null;

	//ライセンスのエラーを表示したまま画面を離れた場合も、閉じたときと同じく確認をやり直す
	//※QRスキャン画面はエラーを閉じると買い物画面へ戻るため、resumeDecoding() が呼ばれない
	licenseScreenReady = false;
	licenseDecodeHeld = false;
	if (licenseState === LICENSE_STATE_NG && licenseErrorShown) {
		retryLicenseAfterError();
	}

	detachHost(hostElement(), slot);

	//scandit と同じく、スキャン画面から離れたらカメラを閉じる
	releaseCamera();

	return true;
}

function onScannerResult(result) {
	if (!scanHandler) return;

	//ライセンス未適用のときは読み取り値がマスクされる。
	//そのままAPIへ送ると「バーコードが読み取れませんでした（04）」になり原因が分からないため、
	//ここで止めてライセンスを再適用する
	if (isUnlicensedResult(result)) {
		handleUnlicensedResult();
		return;
	}

	//正常に読めた＝ライセンスは効いているので状態を戻す
	licenseReapplyDone = false;
	licenseErrorNotified = false;
	if (licenseState !== LICENSE_STATE_UNKNOWN) {
		setLicenseState(LICENSE_STATE_OK, "マスクされていない値を読み取り");
	}

	const typeName = String(result?.barcodeTypeName || "").toLowerCase();
	const isQR = typeName.indexOf("qr") !== -1;

	//表示中の画面が扱う種別以外は無視する
	if (scanMode === "qr" && !isQR) return;
	if (scanMode === "barcode" && isQR) return;

	scanHandler(result);
}

//----------------------------------------
//デコーダ設定の適用
//----------------------------------------
// ライセンスを適用し直すと initialize() が走り直すため、
// カメラ起動時だけでなくライセンス再適用のあとにも当て直す必要がある。

function applyDecoderSettings(instance) {
	if (!instance) return;

	//商品バーコードとQRを同時に有効化し、読み取り種別は結果側で振り分ける
	//（スキャン画面ごとに切り替えるとカメラの再起動が必要になるため）
	instance.setEnabledDecoders(
		instance.constants.Decoders.Code128,
		instance.constants.Decoders.Ean13,
		instance.constants.Decoders.Ean8,
		instance.constants.Decoders.UpcA,
		instance.constants.Decoders.UpcE,
		instance.constants.Decoders.QR
	);
	setDoubleParam(instance, "UPC_EAN_CONVERT_UPC_TO_EAN", false);
	setDoubleParam(instance, "NORMALIZE_OUTPUT", false);
	if (instance.setUpcEanDeblur) instance.setUpcEanDeblur(true);

	//歪んだ1Dバーコードの読取を有効化
	//（ペットボトルや袋物など曲面の商品バーコード対策
	//EnableMisshaped1D: Disable = 0, Enable = 1
	if (instance.setEnableMisshaped1D) {
		const enable = instance.constants?.EnableMisshaped1D?.Enable;
		instance.setEnableMisshaped1D(typeof enable === "number" ? enable : 1);
	}

	//1フレームあたりの解析の厚さ
	//※Slow にすると実機でメインスレッドが飽和し、プレビューが
	//　毎秒4〜5回コマ落ちした（0.1〜0.15秒停止→短く更新の繰り返し）。
	//　曲面商品対策は上の setEnableMisshaped1D が主役なので、
	//　ここは Normal に戻して負荷を下げる
	//DecodingSpeed: Fast = 0, Normal = 1, Slow = 2, Rigorous = 3
	if (instance.setDecodingSpeed) {
		const normal = instance.constants?.DecodingSpeed?.Normal;
		instance.setDecodingSpeed(typeof normal === "number" ? normal : 1);
	}
}

//----------------------------------------
//ライセンス未適用の判定
//----------------------------------------
// ライセンスが効いていない場合、wasmは読み取り値を
// 「(Unlicensed) ...490133...」のようにマスクして返す。

export function isUnlicensedResult(result) {
	const data = String(result?.textualData ?? result?.rawData ?? "");
	return data.indexOf("Unlicensed") !== -1;
}

//----------------------------------------
//ライセンス未適用を検知したときの処理
//----------------------------------------
// 1回でも検知すればライセンスが適用されていないことは確定する
// （barkoderは未適用のときだけマスクするため、誤検知の余地がない）。
//
// ただしエラーを出すのは「自動復帰に一度チャンスを与えたあと」にする。
// 検知した時点では再適用を走らせるだけにして黙って破棄し、
// 再適用が完了した「あと」にまだマスクされた結果が来たら、
// 直らなかったと判断してエラーを通知する。
//
// ※毎秒20回デコードしているため検知は短時間に何度も起きるが、
// 　再適用中の結果はまとめて破棄されるだけで問題にならない

//再適用が1度でも完了したか（完了後も直らなければ通知する
let licenseReapplyDone = false;
//通知済みか（同じ内容を繰り返し出さない
let licenseErrorNotified = false;

function handleUnlicensedResult() {
	//診断用の記録（ログのみ
	licenseDiag.unlicensedCount += 1;
	licenseDiag.lastUnlicensedAt = new Date().toLocaleTimeString();

	//準備の状態を判定できるとき：マスクされた値は未適用の確実な証拠なので、自動の再試行を待たずに
	//エラーを出す。再試行はエラーを閉じたときに行う（ここでは再適用を走らせない
	//※エラーの表示中はマスクされた値が毎フレーム届くため、ログはエラーを出すときだけ
	if (licenseState !== LICENSE_STATE_UNKNOWN) {
		//再試行の最中は結果を待つ（ここでエラーを出すと、再試行の結果が出る前に出し直してしまう
		if (licenseRetryPromise) return;
		if (!licenseErrorShown) {
			console.log("【barkoder】ライセンス未適用の結果を検知した（" + describeUnlicensedContext() + "）");
		}
		setLicenseState(LICENSE_STATE_NG, "マスクされた値を検知");
		if (scanHandler) holdDecodingForLicense();
		return;
	}

	if (licenseReapplyDone) {
		if (licenseErrorNotified) return;
		licenseErrorNotified = true;

		console.log("【barkoder】ライセンスを再適用しても直らないためエラーを通知する（" + describeUnlicensedContext() + "）");
		if (errorHandler) errorHandler(createLicenseError("barkoder license not applied"));
		return;
	}

	console.log("【barkoder】ライセンス未適用の結果を検知したため破棄する（" + describeUnlicensedContext() + "）");

	reapplyLicense().then((done) => {
		if (done) licenseReapplyDone = true;
	});
}

//----------------------------------------
//ライセンスの再適用
//----------------------------------------
// barkoder の initialize() は、ライセンスの確認にサーバー応答が必要な場合、
// それを最大1秒しか待たない実装になっている。
//
//   let response = Connector.initialize(dataPtr);
//   if (typeof window.BarkoderSDK_callback3D == 'undefined') {
//       while (!window.BarkoderSDK_callback2D) {
//           await Common.wait(20); timeCounter += 20;
//           if (timeCounter >= 1000) { timeout = true; break; }   // ← 1秒で打ち切り
//       }
//       if (!timeout) response = Connector.initialize(dataPtr);   // ← 打ち切ると呼ばれない
//   }
//
// 1秒以内に応答が返らないと2回目の initialize() が実行されず、
// ライセンスが適用されないまま「初期化成功」になる。回線が遅いと再現する。
// 応答は届き次第 localStorage にキャッシュされるので、
// 少し後に呼び直せば適用される（次回起動から直る理由もこれ）。
//
// ※短時間に何度も呼ばないよう間隔を空ける

const LICENSE_RETRY_INTERVAL = 5000;
let licenseRetryAt = 0;

//実行中の再適用
//※initialize() を同時に呼ぶと、SDK の目印（callback2D / 3D）を取り合って判定が狂うため1本にする
let reapplyInFlight = null;

export function reapplyLicense(options = {}) {
	if (reapplyInFlight) return reapplyInFlight;

	//※自動の再試行（間隔を空けて呼ぶ）と利用者の再試行は、連打防止の間隔を待たない
	const now = Date.now();
	if (!options.force && now - licenseRetryAt < LICENSE_RETRY_INTERVAL) return Promise.resolve(false);
	licenseRetryAt = now;

	reapplyInFlight = reapplyLicenseInternal().finally(() => {
		reapplyInFlight = null;
	});
	return reapplyInFlight;
}

async function reapplyLicenseInternal() {
	try {
		const BarkoderSDK = await loadBarkoderScript();
		const licenseKey = getBarkoderLicenseKey() || "";

		console.log("【barkoder】ライセンスを再適用する");
		//ライセンス確認の診断（ログのみ）
		//※ここで「done」になっても、サーバー応答が1秒に間に合わなければライセンスは効いていない。
		//　効いたかどうかは下の結果ログ（経路）で判断する
		licenseDiag.reapplyCount += 1;
		const licenseCheck = beginLicenseCheck("再適用" + licenseDiag.reapplyCount + "回目");
		licenseDiag.reapply = licenseCheck;
		const reapplyPromise = BarkoderSDK.initialize(licenseKey);
		reapplyPromise.then(
			() => endLicenseCheck(licenseCheck),
			(e) => endLicenseCheck(licenseCheck, e)
		);
		reapplyPromise.catch(() => {});
		await withTimeout(
			reapplyPromise,
			BARKODER_INIT_TIMEOUT,
			"barkoder license reapply"
		);
		//initialize() が走り直すためデコーダ設定を当て直す
		applyDecoderSettings(barkoder);

		console.log("【barkoder】ライセンス再適用 done");
		return true;
	} catch (e) {
		console.log("【barkoder】ライセンス再適用に失敗: " + e);
		return false;
	}
}

//----------------------------------------
//デコードの一時停止／再開（カメラはそのまま
//----------------------------------------

export function pauseDecoding() {
	decodingActive = false;
	applyPauseDecoding(barkoder, true);
}

export function resumeDecoding() {
	//ライセンスの準備ができていない場合
	//※スキャン画面に入った直後は、読み取りを待たずにエラーを出す。
	//　エラーを閉じたあと（setScanFlg(false) から呼ばれる）は、その場でライセンスの確認だけをやり直す
	if (licenseState === LICENSE_STATE_NG) {
		if (!licenseScreenReady || !licenseErrorShown) {
			licenseScreenReady = true;
			notifyLicenseNotReady();
		} else {
			retryLicenseAfterError();
		}
		//準備できるまで読み取りは止めておく（準備できたら setLicenseState で再開する
		holdDecodingForLicense();
		return;
	}
	licenseScreenReady = true;

	resumeDecodingInternal();
}

//デコード再開（SDK 内部の立て直し用。ライセンスの判断には関わらない）
//※restartScanner などの内部処理からも再開するため、スキャン画面からの再開（resumeDecoding）と分けている。
//　分けないと、内部の立て直しを「エラーを閉じた」と取り違えて確認をやり直してしまう（2026-10-27 Dev で確認）
function resumeDecodingInternal() {
	decodingActive = true;
	applyPauseDecoding(barkoder, false);
}

function applyPauseDecoding(instance, pause) {
	if (!instance || typeof instance.setPauseDecoding !== "function") return;
	try {
		instance.setPauseDecoding(pause);
	} catch (e) {
		console.log("【barkoder】setPauseDecoding error: " + e);
	}
}


//----------------------------------------
//数値パラメータ設定（未対応パラメータは無視
//----------------------------------------

export function setDoubleParam(instance, param, value) {
	const p = instance?.constants?.Parameters?.[param];
	if (p === undefined) {
		console.log("【barkoder】Parameter not supported → " + param);
		return;
	}
	try {
		instance.setDoubleParameter(p, value);
	} catch (e) {
		console.log("【barkoder】setDoubleParameter error: " + e);
	}
}

//----------------------------------------
//stopScanner（Promise/同期どちらでも確実に握りつぶす
//----------------------------------------

export async function safeStopScanner(instance) {
	if (!instance?.stopScanner) return;
	try {
		await Promise.resolve(instance.stopScanner());
	} catch (e) {
		console.log("【barkoder】stopScanner error: " + e);
	}
}

//----------------------------------------
//video要素の物理停止
//----------------------------------------

export function stopVideoElement(video) {
	try {
		const stream = video.srcObject;
		if (stream && typeof stream.getTracks === "function") {
			stream.getTracks().forEach((track) => {
				try {
					track.stop();
				} catch (e) {}
			});
		}
	} catch (e) {}

	// ※srcObject は外さない。pause() もしない。
	// 　barkoder はプレビューのvideo要素を初回に1つ作って以降ずっと使い回す
	// 　（createPreview が if(!this.initialized) で囲われている）。
	// 　ここで srcObject を null にすると、SDKが持っている参照との整合が崩れ、
	// 　次に startScanner() しても getUserMedia の中の
	// 　「video.srcObject = mediaStream」に到達しない限り復帰できなくなる。
	// 　しかも start() は runningState.active が立っていると何もせず返るため、
	// 　プレビューが空のまま「起動成功」になってしまう。
	// 　トラックの停止はSDKの stopPreview() 自身も行っている（videoTrack.stop()）ので、
	// 　ここは保険としてトラックを止めるだけにとどめる。
	// video.load() も barkoder のDOM参照を壊すため呼ばない
}

//----------------------------------------
//カメラトラックの停止（container外に生えたvideoも対象
//----------------------------------------
// 以前は srcObject を null にして「確実に止める」実装だったが、
// それが原因でプレビューが復帰しなくなるため取りやめた（stopVideoElement のコメント参照）。
// 現在はトラックを止めるだけで、DOMとSDKの参照関係には触らない。

export function hardStopMediaStream(container, lastStream) {
	try {
		if (lastStream && typeof lastStream.getTracks === "function") {
			lastStream.getTracks().forEach((track) => {
				try {
					track.stop();
				} catch (e) {}
			});
		}

		const videos = container ? container.querySelectorAll("video") : [];
		videos.forEach((video) => stopVideoElement(video));

		document.querySelectorAll("video").forEach((video) => {
			if (container && container.contains(video)) return;

			const id = (video.id || "").toLowerCase();
			const cls = (video.className || "").toString().toLowerCase();
			const parentId = video.parentElement?.id ? video.parentElement.id.toLowerCase() : "";

			const looksBarkoder =
				id.indexOf("barkoder") !== -1 ||
				cls.indexOf("barkoder") !== -1 ||
				parentId.indexOf("barkoder") !== -1 ||
				(video.getAttribute("playsinline") !== null && video.muted === true);

			if (!looksBarkoder) return;

			stopVideoElement(video);
		});
	} catch (e) {
		console.log("【barkoder】hardStopMediaStream error: " + e);
	}
}

//----------------------------------------
//JAN13補正：12桁のUPC-A を JAN13 に補正（先頭0を補完
//----------------------------------------

export function fixUPCEAN(code) {
	if (!code) return code;
	if (code.length === 13) return code;

	if (code.length === 12) {
		//JAN13の国コード候補
		const janCountryCodes = ["45", "49", "04", "02", "01", "03", "07"];
		for (const cc of janCountryCodes) {
			if (code.startsWith(cc[1])) {
				//例：460041903850 → 本来 0460041903850
				return "0" + code;
			}
		}
		//該当しない場合は UPC-A と判断 → 補正しない
		return code;
	}

	return code;
}
